import { Trans } from "react-i18next";
import React, { useState, useEffect } from "react";
import { Alert, Button, Form } from "react-bootstrap";
import ExclamationTriangle from "react-bootstrap-icons/dist/icons/exclamation-triangle";
import "./ChangeSummaryBanner.css";

export interface ConfigChange {
  parameter: string;
  oldValue: string | number;
  newValue: string | number;
  category: "montage" | "filter" | "gain" | "timebase" | "other";
}

interface ChangeSummaryBannerProps {
  changes: ConfigChange[];
  isClinicalMode: boolean;
  onAcknowledge: () => void;
  onRevert: () => void;
  autoHideMs?: number; // Default 3000 for Research, never for Clinical
}

export const ChangeSummaryBanner: React.FC<ChangeSummaryBannerProps> = ({
  changes,
  isClinicalMode,
  onAcknowledge,
  onRevert,
  autoHideMs = 3000,
}) => {
  const [visible, setVisible] = useState(true);
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    if (!isClinicalMode && autoHideMs > 0) {
      const timer = setTimeout(() => setVisible(false), autoHideMs);
      return () => clearTimeout(timer);
    }
  }, [isClinicalMode, autoHideMs]);

  useEffect(() => {
    // Reset acknowledged state when changes update
    setAcknowledged(false);
  }, [changes]);

  if (!visible || changes.length === 0) return null;

  const criticalChanges = changes.filter(
    (c) => c.category === "montage" || c.category === "filter",
  );

  const handleAcknowledge = () => {
    onAcknowledge();
    setVisible(false);
  };

  const handleRevert = () => {
    onRevert();
    setVisible(false);
  };

  return (
    <Alert
      variant="warning"
      dismissible={!isClinicalMode}
      onClose={isClinicalMode ? undefined : () => setVisible(false)}
      className="change-summary-banner"
      role="alert"
      aria-live="assertive"
    >
      <div className="change-summary-banner__header">
        <ExclamationTriangle size={20} aria-label="Warning" role="img" />
        <strong><Trans ns="features" i18nKey="ui.copy.0e8a3d422297" /></strong>
      </div>

      <ul className="change-summary-banner__changes" id="change-summary-list">
        {changes.map((change, idx) => (
          <li key={idx} className="change-item">
            <span className="change-item__param">{change.parameter}:</span>
            <span className="change-item__old" aria-label="previous value">
              {String(change.oldValue)}
            </span>
            <span className="change-item__arrow" aria-hidden="true">
              →
            </span>
            <span className="change-item__new" aria-label="new value">
              {String(change.newValue)}
            </span>
          </li>
        ))}
      </ul>

      {isClinicalMode && criticalChanges.length > 0 && (
        <Form.Check
          type="checkbox"
          id="acknowledge-changes"
          label="I acknowledge these signal parameter changes and wish to continue"
          checked={acknowledged}
          onChange={(e) => setAcknowledged(e.target.checked)}
          className="mt-2"
          aria-describedby="change-summary-list"
        />
      )}

      <div className="change-summary-banner__actions mt-2">
        <Button variant="outline-secondary" size="sm" onClick={handleRevert}>
          <Trans ns="features" i18nKey="ui.copy.272607a7bd01" /></Button>
        <Button
          variant="warning"
          size="sm"
          onClick={handleAcknowledge}
          disabled={
            isClinicalMode && criticalChanges.length > 0 && !acknowledged
          }
          aria-describedby={
            isClinicalMode && criticalChanges.length > 0
              ? "acknowledge-changes"
              : undefined
          }
        >
          <Trans ns="features" i18nKey="ui.copy.362bcdb709cf" /></Button>
      </div>
    </Alert>
  );
};

export default ChangeSummaryBanner;
